import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import Tabs from './ui/Tabs';
import { whosInService } from '../services/whosInService';
import WhosInSunburst from './whosIn/WhosInSunburst';
import WhosInForecastTimeline from './whosIn/WhosInForecastTimeline';
import WhosInEmployeeScheduleModal from './whosIn/WhosInEmployeeScheduleModal';
import { fetchHolidaysForRange } from '../utils/whosIn/canadaHolidays';
import { formatNextMovementLabel } from '../utils/whosIn/nextMovement';
import './WhosIn.css';

function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function groupByTeam(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const team = row.team?.trim() || 'Unassigned';
    if (!groups.has(team)) groups.set(team, []);
    groups.get(team).push(row);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([team, people]) => ({
      team,
      people: [...people].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    }));
}

function TeamRosterCards({
  rows,
  emptyMessage,
  showFirstVisit = true,
  showNextMovement = false,
  viewDate,
  searchTerm = '',
  onPersonClick,
}) {
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((person) => (person.name || '').toLowerCase().includes(term));
  }, [rows, searchTerm]);

  const teamGroups = useMemo(() => groupByTeam(filteredRows), [filteredRows]);

  if (rows.length === 0) {
    return (
      <div className="no-data whos-in-tab-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  if (filteredRows.length === 0) {
    return (
      <div className="no-data whos-in-tab-empty">
        <p>No names match &ldquo;{searchTerm.trim()}&rdquo;.</p>
      </div>
    );
  }

  return (
    <div className="whos-in-team-roster">
      {teamGroups.map(({ team, people }) => (
        <article key={team} className="whos-in-team-card">
          <header className="whos-in-team-card-header">
            <h3 className="whos-in-team-card-title">{team}</h3>
            <span className="whos-in-team-card-count">{people.length}</span>
          </header>
          <ul className="whos-in-team-list">
            {people.map((person) => (
              <li
                key={`${person.schedule_row_id ?? person.employee_id}-${person.name}`}
                className={`whos-in-team-row${person.is_first_on_site ? ' whos-in-row-first-visit' : ''}`}
              >
                <div className="whos-in-person-line">
                  {onPersonClick ? (
                    <button
                      type="button"
                      className="whos-in-person-name whos-in-person-name-btn"
                      onClick={() => onPersonClick(person)}
                    >
                      {person.name}
                    </button>
                  ) : (
                    <span className="whos-in-person-name">{person.name}</span>
                  )}
                  {showFirstVisit && person.is_first_on_site && (
                    <span className="whos-in-first-visit-badge">New</span>
                  )}
                </div>
                {person.position && (
                  <span className="whos-in-person-meta">{person.position}</span>
                )}
                {showNextMovement && person.next_movement_date && (
                  <span
                    className={`whos-in-person-meta whos-in-person-movement whos-in-movement-${person.next_movement_type}`}
                  >
                    {formatNextMovementLabel(
                      person.next_movement_type,
                      person.next_movement_date,
                      viewDate,
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

const WhosIn = ({ user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewDate, setViewDate] = useState(todayLocalISO);
  const [holidayMap, setHolidayMap] = useState(new Map());
  const [rosterSearch, setRosterSearch] = useState('');
  const [activeRosterTab, setActiveRosterTab] = useState('onsite');
  const [scheduleModalPerson, setScheduleModalPerson] = useState(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const payload = await whosInService.getDashboardPayload({ date: viewDate });
      setDashboard(payload);
    } catch (err) {
      console.error("❌ Error loading who's in data:", err);
      setError(err.message || "Failed to load who's in data");
    } finally {
      setLoading(false);
    }
  }, [viewDate]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const forecastRange = dashboard?.forecastRange;

  useEffect(() => {
    if (!forecastRange?.from || !forecastRange?.to) return undefined;
    let cancelled = false;
    fetchHolidaysForRange(forecastRange.from, forecastRange.to, 'BC').then((map) => {
      if (!cancelled) setHolidayMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [forecastRange?.from, forecastRange?.to]);

  const daySummary =
    dashboard?.summaries && dashboard.summaries.length === 1
      ? dashboard.summaries[0]
      : null;
  const logisticDay =
    dashboard?.logisticsDaily && dashboard.logisticsDaily.length === 1
      ? dashboard.logisticsDaily[0]
      : null;

  const onSiteRosterRaw = dashboard?.onSiteRoster ?? [];
  const onSiteRoster =
    dashboard?.onSiteRosterEnriched ?? dashboard?.onSiteRoster ?? [];
  const rosterRows = dashboard?.fullRoster ?? dashboard?.roster ?? [];
  const forecast = dashboard?.forecast ?? [];

  const onSiteCount =
    onSiteRosterRaw.length > 0
      ? onSiteRosterRaw.length
      : (daySummary?.on_site_count ?? dashboard?.summarySnapshot?.on_site_count ?? 0);
  const traveling =
    daySummary?.travel_count ?? dashboard?.summarySnapshot?.travel_count ?? '—';
  const inbound =
    logisticDay?.inbound_count ?? daySummary?.inbound_count ?? '—';
  const outbound =
    logisticDay?.outbound_count ?? daySummary?.outbound_count ?? '—';

  const handlePersonClick = useCallback((person) => {
    setScheduleModalPerson(person);
  }, []);

  const rosterTabs = useMemo(
    () => [
      {
        id: 'onsite',
        label: 'On-site roster',
        iconId: ICON.whosinTabOnsite,
        badge: onSiteCount || null,
        content: (
          <div className="whos-in-tab-panel">
            <TeamRosterCards
              rows={onSiteRoster}
              emptyMessage={`No one on site for ${viewDate}.`}
              showNextMovement
              viewDate={viewDate}
              searchTerm={rosterSearch}
              onPersonClick={handlePersonClick}
            />
          </div>
        ),
      },
      {
        id: 'sunburst',
        label: 'Breakdown',
        iconId: ICON.whosinTabBreakdown,
        badge: onSiteCount || null,
        content: (
          <div className="whos-in-tab-panel">
            <WhosInSunburst rows={onSiteRosterRaw} viewDate={viewDate} />
          </div>
        ),
      },
      {
        id: 'full',
        label: 'Full roster',
        iconId: ICON.whosinTabFullRoster,
        badge: rosterRows.length || null,
        content: (
          <div className="whos-in-tab-panel">
            <TeamRosterCards
              rows={rosterRows}
              emptyMessage="No active employees found."
              showFirstVisit={false}
              showNextMovement
              viewDate={viewDate}
              searchTerm={rosterSearch}
              onPersonClick={handlePersonClick}
            />
          </div>
        ),
      },
    ],
    [
      onSiteRoster,
      onSiteRosterRaw,
      onSiteCount,
      viewDate,
      rosterRows,
      rosterSearch,
      handlePersonClick,
    ],
  );

  const importBanner = dashboard?.imports
    ? `${dashboard.imports.import_date || 'unknown time'} • ${dashboard.imports.import_status || 'status'} • processed ${dashboard.imports.records_processed ?? '—'}`
    : 'No recent import recorded';

  const todayIso = todayLocalISO();
  const showRosterSearch = activeRosterTab === 'onsite' || activeRosterTab === 'full';

  return (
    <Layout user={user} currentRoute="/whos-in">
      <div className="whos-in">
        <div className="whos-in-header">
          <IconText icon={ICON.appWhosIn} as="h1">Who's In?</IconText>
          <p className="whos-in-subtitle">
            <span>Camp and site roster from the synced schedule ({viewDate}).</span>
            {dashboard && (
              <span className="whos-in-subtitle-import">
                Last import: {importBanner}
              </span>
            )}
          </p>
        </div>

        {loading && !dashboard && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading roster data...</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </Card>
        )}

        {!error && dashboard && (
          <>
            <div className="whos-in-overview-bar">
              <div className="whos-in-overview-stat whos-in-overview-stat-light">
                <span className="whos-in-overview-value">{onSiteCount}</span>
                <span className="whos-in-overview-label">On site</span>
              </div>
              <div className="whos-in-overview-stat whos-in-overview-stat-dark">
                <span className="whos-in-overview-value">{traveling}</span>
                <span className="whos-in-overview-label">Travel</span>
              </div>
              <div className="whos-in-overview-stat whos-in-overview-stat-light">
                <span className="whos-in-overview-value">{inbound}</span>
                <span className="whos-in-overview-label">Inbound</span>
              </div>
              <div className="whos-in-overview-stat whos-in-overview-stat-dark">
                <span className="whos-in-overview-value">{outbound}</span>
                <span className="whos-in-overview-label">Outbound</span>
              </div>
            </div>

            <Card className="content-card whos-in-forecast-card">
              <WhosInForecastTimeline
                forecast={forecast}
                viewDate={viewDate}
                onViewDateChange={setViewDate}
                onRefresh={loadDashboardData}
                refreshing={loading}
                holidayMap={holidayMap}
                todayIso={todayIso}
                loading={loading}
                onSelectDate={setViewDate}
                forecastRange={forecastRange}
              />
            </Card>

            <Card className="content-card whos-in-tabs-card">
              <div className="whos-in-tabs-card-header">
                <h2>On-site roster — {viewDate}</h2>
                {showRosterSearch && (
                  <input
                    type="search"
                    className="whos-in-roster-search"
                    placeholder="Search by name…"
                    value={rosterSearch}
                    onChange={(e) => setRosterSearch(e.target.value)}
                    aria-label="Search roster by name"
                  />
                )}
              </div>
              <Tabs
                tabs={rosterTabs}
                defaultTab="onsite"
                className="whos-in-tabs"
                onTabChange={setActiveRosterTab}
              />
            </Card>

            {scheduleModalPerson && forecastRange?.from && forecastRange?.to && (
              <WhosInEmployeeScheduleModal
                person={scheduleModalPerson}
                viewDate={viewDate}
                rangeFrom={forecastRange.from}
                rangeTo={forecastRange.to}
                holidayMap={holidayMap}
                todayIso={todayIso}
                onClose={() => setScheduleModalPerson(null)}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default WhosIn;
